/**
 * OllamaModelManager Component
 *
 * Full-screen modal for managing Ollama models.
 * Shows installed models with sizes, available models with VRAM requirements,
 * and disables models incompatible with current hardware.
 */

import { useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import {
  X,
  Download,
  Trash2,
  HardDrive,
  Cpu,
  CheckCircle,
  AlertCircle,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import type { OllamaModel, DownloadProgress } from '../../hooks/useOllamaModels';
import type { HardwareCapabilities } from '../../services/componentService';

interface OllamaModelManagerProps {
  isOpen: boolean;
  onClose: () => void;
  models: OllamaModel[];
  loading: boolean;
  ollamaAvailable: boolean;
  downloadProgress: Record<string, DownloadProgress>;
  hardware: HardwareCapabilities | null;
  onDownload: (modelId: string) => Promise<boolean>;
  onDelete: (modelId: string) => Promise<boolean>;
  onRefresh: () => Promise<void>;
  onClearProgress: (modelId: string) => void;
}

function getHardwareRecommendation(vramGb: number): { label: string; color: string } {
  if (vramGb <= 2) return { label: '4GB+ GPU', color: 'text-green-400' };
  if (vramGb <= 4) return { label: '6GB+ GPU', color: 'text-green-400' };
  if (vramGb <= 6) return { label: '8GB+ GPU', color: 'text-blue-400' };
  if (vramGb <= 10) return { label: '12GB+ GPU', color: 'text-yellow-400' };
  if (vramGb <= 24) return { label: '24GB+ GPU', color: 'text-orange-400' };
  return { label: '48GB+ GPU', color: 'text-red-400' };
}

export function OllamaModelManager({
  isOpen,
  onClose,
  models,
  loading,
  ollamaAvailable,
  downloadProgress,
  hardware,
  onDownload,
  onDelete,
  onRefresh,
  onClearProgress,
}: OllamaModelManagerProps) {
  const { t } = useTranslation();
  const [deletingModels, setDeletingModels] = useState<Record<string, boolean>>({});
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  if (!isOpen) return null;

  const userVram = hardware?.gpu?.vramGb ?? 0;
  const hasGpu = userVram > 0;

  const installedModels = models.filter((m) => m.installed);
  const availableModels = models.filter((m) => !m.installed);

  const canRunModel = (model: OllamaModel): boolean => {
    if (!hasGpu) return model.vram_gb <= 2; // CPU-only can run very small models
    return model.vram_gb <= userVram;
  };

  const handleDelete = async (modelId: string) => {
    setConfirmDelete(null);
    setDeletingModels((prev) => ({ ...prev, [modelId]: true }));
    try {
      await onDelete(modelId);
    } finally {
      setDeletingModels((prev) => ({ ...prev, [modelId]: false }));
    }
  };

  const handleDownload = async (modelId: string) => {
    await onDownload(modelId);
  };

  const totalInstalledSize = installedModels.reduce((sum, m) => sum + m.size_gb, 0);

  const renderProgressBar = (modelId: string) => {
    const progress = downloadProgress[modelId];
    if (!progress) return null;

    const isDownloading = progress.status === 'downloading' || progress.status === 'finalizing';
    const isCompleted = progress.status === 'completed' || progress.status === 'already_installed';
    const isError = progress.status === 'error';

    if (!isDownloading && !isCompleted && !isError) return null;

    return (
      <div className="mt-2">
        {isDownloading && (
          <div>
            <div className="w-full bg-black/30 rounded-full h-2 overflow-hidden">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress.progress}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-white/50 mt-1">
              <span className="flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                {progress.progress}%
              </span>
              <div className="flex gap-3">
                {progress.speed && <span>{progress.speed}</span>}
                {progress.eta && <span>ETA: {progress.eta}</span>}
              </div>
            </div>
          </div>
        )}
        {isCompleted && (
          <div className="flex items-center gap-2 text-xs text-green-400">
            <CheckCircle className="w-3.5 h-3.5" />
            <span>
              {t('ollama.downloadCompleteMessage', 'Download complete! Model is now available.')}
            </span>
            <button
              onClick={() => onClearProgress(modelId)}
              className="ml-auto text-white/40 hover:text-white/70 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        {isError && (
          <div className="flex items-center gap-2 text-xs text-red-400">
            <AlertCircle className="w-3.5 h-3.5" />
            <span>{progress.message || t('ollama.downloadError', 'Download failed')}</span>
            <button
              onClick={() => onClearProgress(modelId)}
              className="ml-auto text-white/40 hover:text-white/70 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderModelRow = (model: OllamaModel, isInstalled: boolean) => {
    const compatible = canRunModel(model);
    const hwRec = getHardwareRecommendation(model.vram_gb);
    const isDeleting = deletingModels[model.id] ?? false;
    const isDownloading =
      downloadProgress[model.id]?.status === 'downloading' ||
      downloadProgress[model.id]?.status === 'finalizing';

    return (
      <div
        key={model.id}
        className={`p-3 rounded-lg border transition-colors ${
          !compatible && !isInstalled
            ? 'border-white/5 bg-black/10 opacity-50'
            : 'border-white/10 bg-black/20 hover:bg-black/30'
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          {/* Model info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-white/90 text-sm">{model.id}</span>
              {!compatible && !isInstalled && (
                <span className="inline-flex items-center gap-1 text-xs text-amber-400">
                  <AlertTriangle className="w-3 h-3" />
                  {t('ollamaManager.incompatible', 'Incompatible')}
                </span>
              )}
            </div>
            <p className="text-xs text-white/50 mt-0.5 truncate">{model.description}</p>
            <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-white/40">
              <span className="flex items-center gap-1">
                <HardDrive className="w-3 h-3" />
                {model.size_gb} GB
              </span>
              <span className="flex items-center gap-1">
                <Cpu className="w-3 h-3" />
                {model.vram_gb} GB VRAM
              </span>
              <span className={`${hwRec.color}`}>{hwRec.label}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex-shrink-0">
            {isInstalled ? (
              <div className="flex items-center gap-2">
                {confirmDelete === model.id ? (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleDelete(model.id)}
                      disabled={isDeleting}
                      className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors min-h-[32px]"
                    >
                      {t('common.confirm', 'Confirm')}
                    </button>
                    <button
                      onClick={() => setConfirmDelete(null)}
                      className="px-2 py-1 text-xs text-white/60 hover:text-white/90 transition-colors min-h-[32px]"
                    >
                      {t('common.cancel', 'Cancel')}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDelete(model.id)}
                    disabled={isDeleting}
                    className="p-2 text-white/40 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                    title={t('ollama.deleteModel', 'Delete Model')}
                  >
                    {isDeleting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
            ) : (
              <button
                onClick={() => handleDownload(model.id)}
                disabled={!compatible || isDownloading}
                className={`p-2 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center ${
                  compatible && !isDownloading
                    ? 'text-white/60 hover:text-blue-400 hover:bg-blue-900/20'
                    : 'text-white/20 cursor-not-allowed'
                }`}
                title={
                  !compatible
                    ? t('ollamaManager.insufficientHardware', 'Insufficient VRAM for this model')
                    : t('ollama.download', 'Download')
                }
              >
                {isDownloading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        {renderProgressBar(model.id)}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-secondary-900 border border-white/10 rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-white/10 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-white/90">
              {t('ollamaManager.title', 'Ollama Model Manager')}
            </h2>
            {hasGpu && (
              <p className="text-xs text-white/40 mt-0.5">
                {t('ollamaManager.yourGpu', 'Your GPU')}: {hardware?.gpu?.name || 'Unknown'} (
                {userVram} GB VRAM)
              </p>
            )}
            {!hasGpu && (
              <p className="text-xs text-amber-400/70 mt-0.5">
                {t(
                  'ollamaManager.noGpuDetected',
                  'No GPU detected - only small models available (CPU mode)'
                )}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/40 hover:text-white/80 hover:bg-white/5 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-6">
          {!ollamaAvailable ? (
            <div className="text-center py-8">
              <AlertCircle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
              <p className="text-white/70 font-medium">
                {t('ollama.notAvailable', 'Ollama is not available')}
              </p>
              <p className="text-sm text-white/40 mt-1">
                {t('ollama.installOllama', 'Install Ollama and open it to use local models')}
              </p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-white/40 animate-spin" />
            </div>
          ) : (
            <>
              {/* Installed Models */}
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wider">
                    {t('ollamaManager.installedModels', 'Installed Models')}
                    <span className="ml-2 text-white/30 font-normal normal-case">
                      ({installedModels.length})
                    </span>
                  </h3>
                  {installedModels.length > 0 && (
                    <span className="text-xs text-white/40">
                      {t('ollamaManager.totalSize', 'Total')}: {totalInstalledSize.toFixed(1)} GB
                    </span>
                  )}
                </div>
                {installedModels.length === 0 ? (
                  <div className="p-4 rounded-lg border border-white/5 bg-black/10 text-center">
                    <p className="text-sm text-white/40">
                      {t(
                        'ollamaManager.noInstalledModels',
                        'No models installed yet. Download one below to get started.'
                      )}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {installedModels.map((model) => renderModelRow(model, true))}
                  </div>
                )}
              </section>

              {/* Available Models */}
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wider">
                    {t('ollamaManager.availableModels', 'Available Models')}
                    <span className="ml-2 text-white/30 font-normal normal-case">
                      ({availableModels.length})
                    </span>
                  </h3>
                  <button
                    onClick={onRefresh}
                    className="text-xs text-white/40 hover:text-white/70 transition-colors"
                  >
                    {t('additionalInstallations.refresh', 'Refresh')}
                  </button>
                </div>
                {availableModels.length === 0 ? (
                  <div className="p-4 rounded-lg border border-white/5 bg-black/10 text-center">
                    <p className="text-sm text-white/40">
                      {t(
                        'ollamaManager.allModelsInstalled',
                        'All available models are already installed.'
                      )}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Compatible models first, then incompatible */}
                    {availableModels
                      .sort((a, b) => {
                        const aCompat = canRunModel(a) ? 0 : 1;
                        const bCompat = canRunModel(b) ? 0 : 1;
                        if (aCompat !== bCompat) return aCompat - bCompat;
                        return a.size_gb - b.size_gb;
                      })
                      .map((model) => renderModelRow(model, false))}
                  </div>
                )}
              </section>

              {/* Hardware legend */}
              <section className="pt-2 border-t border-white/5">
                <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-2">
                  {t('ollamaManager.hardwareGuide', 'Hardware Guide')}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                  <span className="text-green-400">4-6GB GPU: 1-4B models</span>
                  <span className="text-blue-400">8GB GPU: 4-9B models</span>
                  <span className="text-yellow-400">12GB GPU: 9-13B models</span>
                  <span className="text-orange-400">24GB GPU: 13-34B models</span>
                  <span className="text-red-400">48GB+ GPU: 70B+ models</span>
                  <span className="text-white/30">CPU only: 1-2B models</span>
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default OllamaModelManager;
